/**
 * HomeSocialFeed Component
 * Simplified version for homepage integration with dynamic profile fetching
 */

import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Heart, 
  Send, 
  MoreVertical,
  Clock,
  Video,
  Image as ImageIcon,
  ArrowRight,
  User,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../core/contexts/AuthContext';
import { updateDoc, arrayUnion, arrayRemove, doc, increment } from 'firebase/firestore';
import { db } from '../../../services/firebase/firebase';
import { getUserProfile } from '../../../services/firebase/profileService';
import socialService from '../services/socialService';

/**
 * HomeSocialFeed Component
 * Simplified social feed for the homepage with dynamic profile data
 */
const HomeSocialFeed = () => {
  const { currentUser, userProfile } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPost, setExpandedPost] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [authorProfiles, setAuthorProfiles] = useState({});
  const [loadingProfiles, setLoadingProfiles] = useState(new Set());
  
  // Fetch author profile data
  const fetchAuthorProfile = async (authorId) => {
    if (authorProfiles[authorId] || loadingProfiles.has(authorId)) {
      return; // Already have the profile or currently loading
    }

    setLoadingProfiles(prev => new Set(prev).add(authorId));

    try {
      const profile = await getUserProfile(authorId);
      if (profile) {
        setAuthorProfiles(prev => ({
          ...prev,
          [authorId]: {
            avatar: profile.photos?.[0] || '',
            name: profile.basicInfo?.name || 'Anonymous',
            username: profile.basicInfo?.username || '',
            university: profile.academic?.university || '',
            lastUpdated: Date.now()
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching author profile:', error);
    } finally {
      setLoadingProfiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(authorId);
        return newSet;
      });
    }
  };

  // Get author display data (use fetched profile if available, fallback to stored data)
  const getAuthorDisplayData = (post) => {
    const currentProfile = authorProfiles[post.authorId];
    const storedAuthor = post.author;

    if (currentProfile) {
      return currentProfile;
    }

    // Trigger profile fetch if we don't have it
    if (!loadingProfiles.has(post.authorId)) {
      fetchAuthorProfile(post.authorId);
    }

    // Return stored data as fallback
    return {
      avatar: storedAuthor?.avatar || '',
      name: storedAuthor?.name || 'Anonymous',
      username: storedAuthor?.username || '',
      university: storedAuthor?.university || ''
    };
  };
  
  // Fetch initial posts
  const fetchInitialPosts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get only 3 posts for the homepage preview
      const { posts, lastVisible, hasMore } = await socialService.getFeedPosts(3);
      setPosts(posts);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  // Fetch profiles for all visible posts
  useEffect(() => {
    const uniqueAuthorIds = [...new Set(posts.map(post => post.authorId))];
    uniqueAuthorIds.forEach(authorId => {
      if (!authorProfiles[authorId] && !loadingProfiles.has(authorId)) {
        fetchAuthorProfile(authorId);
      }
    });
  }, [posts]);
  
  // Load initial posts on component mount
  useEffect(() => {
    fetchInitialPosts();
  }, []);
  
  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchInitialPosts();
    setIsRefreshing(false);
  };

  // Toggle post expansion (like/comment section)
  const togglePostExpansion = (postId) => {
    setExpandedPost(expandedPost === postId ? null : postId);
  };

  // Handle like/unlike a post
  const handleLikePost = async (post) => {
    if (!currentUser) return;
    
    const postRef = doc(db, 'posts', post.id);
    const userId = currentUser.uid;
    const isLiked = post.likes && post.likes.includes(userId);
    
    try {
      await updateDoc(postRef, {
        likes: isLiked ? arrayRemove(userId) : arrayUnion(userId),
        likesCount: isLiked ? increment(-1) : increment(1)
      });
      
      // Update local post data for immediate UI feedback
      setPosts(prevPosts => 
        prevPosts.map(p => {
          if (p.id === post.id) {
            const newLikes = isLiked 
              ? (p.likes || []).filter(id => id !== userId)
              : [...(p.likes || []), userId];
            
            return {
              ...p,
              likes: newLikes,
              likesCount: (p.likesCount || 0) + (isLiked ? -1 : 1)
            };
          }
          return p;
        })
      );
    } catch (error) {
      console.error('Error updating like:', error);
    }
  };

  // Determine media type icon
  const getMediaTypeIcon = (type) => {
    switch (type) {
      case 'video':
        return <Video size={16} className="mr-1" />;
      case 'image':
        return <ImageIcon size={16} className="mr-1" />;
      default:
        return null;
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Recently';
    return socialService.formatTimestamp(timestamp);
  };

  // Render loading state
  if (loading && posts.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-green-500 border-t-transparent"></div>
        </div>
        <p className="text-center text-gray-500 text-sm">Loading posts...</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <p className="text-red-500 text-sm">{error}</p>
        <button 
          onClick={fetchInitialPosts}
          className="mt-2 text-green-500 text-sm font-medium"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Render empty state
  if (posts.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 text-center">
        <div className="text-gray-400 mb-3">
          <MessageSquare size={36} className="mx-auto" />
        </div>
        <h3 className="text-gray-700 font-medium mb-2">No posts yet</h3>
        <p className="text-gray-500 text-sm mb-3">Be the first to share something with your peers!</p>
        <Link 
          to="/social" 
          className="inline-flex items-center justify-center px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600"
        >
          Go to Social
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Section Header */}
      <div className="flex items-center justify-between px-1 mb-2">
        <h2 className="text-lg font-semibold text-gray-800">Recent Posts</h2>
        <Link to="/social" className="text-sm text-green-500 flex items-center">
          View All <ChevronRight size={16} />
        </Link>
      </div>
      
      {/* Posts */}
      {posts.map((post) => {
        // Get current author display data
        const authorData = getAuthorDisplayData(post);
        
        return (
          <div 
            key={post.id}
            className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm"
          >
            {/* Post Header */}
            <div className="flex items-center justify-between p-3">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full overflow-hidden mr-2 relative">
                  {authorData.avatar ? (
                    <>
                      <img 
                        src={authorData.avatar} 
                        alt={authorData.name || 'User'} 
                        className="w-full h-full object-cover"
                      />
                      {/* Loading indicator for profile update */}
                      {loadingProfiles.has(post.authorId) && (
                        <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                          <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-500 relative">
                      <User size={16} />
                      {loadingProfiles.has(post.authorId) && (
                        <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                          <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <div className="font-medium text-gray-900 text-sm">
                    {authorData.name || 'Anonymous'}
                  </div>
                  <div className="text-xs text-gray-500 flex items-center">
                    <span className="flex items-center">
                      <Clock size={10} className="mr-1" />
                      {formatTimestamp(post.createdAt)}
                    </span>
                    {authorData.username && (
                      <>
                        <span className="mx-1">•</span>
                        <span>@{authorData.username}</span>
                      </>
                    )}
                    {authorData.university && (
                      <>
                        <span className="mx-1">•</span>
                        <span className="text-blue-600 text-xs">{authorData.university}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Post Content - Truncated for homepage */}
            <div className="px-3 py-2">
              <p className="text-gray-800 text-sm whitespace-pre-line line-clamp-3">
                {post.content}
              </p>
              {post.content && post.content.length > 150 && (
                <Link to={`/social/post/${post.id}`} className="text-green-500 text-xs">
                  Read more
                </Link>
              )}
            </div>

            {/* Post Media - Constrained size for homepage */}
            {post.media && (
              <div className="relative">
                {post.media.type === 'image' ? (
                  <div className="max-h-44 overflow-hidden">
                    <img 
                      src={post.media.url} 
                      alt="Post attachment" 
                      className="w-full h-auto object-cover"
                      loading="lazy"
                    />
                  </div>
                ) : post.media.type === 'video' ? (
                  <div className="max-h-44 overflow-hidden relative">
                    <video 
                      src={post.media.url} 
                      className="w-full h-44 object-cover"
                      preload="metadata"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                      <Link 
                        to={`/social/post/${post.id}`}
                        className="bg-white bg-opacity-80 rounded-full p-2"
                      >
                        <Video className="h-8 w-8 text-green-500" />
                      </Link>
                    </div>
                  </div>
                ) : null}
                <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded-full flex items-center">
                  {getMediaTypeIcon(post.media.type)}
                  {post.media.type.charAt(0).toUpperCase() + post.media.type.slice(1)}
                </div>
              </div>
            )}

            {/* Post Footer - Simplified */}
            <div className="px-3 py-2 border-t border-gray-100 flex justify-between">
              <button 
                className={`flex items-center ${
                  post.likes && currentUser && post.likes.includes(currentUser.uid) 
                    ? 'text-red-500' 
                    : 'text-gray-500 hover:text-red-500'
                }`}
                onClick={() => handleLikePost(post)}
              >
                <Heart size={16} className="mr-1" fill={post.likes && currentUser && post.likes.includes(currentUser.uid) ? "currentColor" : "none"} />
                <span className="text-xs">{post.likesCount || 0}</span>
              </button>
              
              <Link 
                to={`/social/post/${post.id}`}
                className="flex items-center text-gray-500 hover:text-blue-500"
              >
                <MessageSquare size={16} className="mr-1" />
                <span className="text-xs">{post.commentsCount || 0}</span>
              </Link>
            </div>
          </div>
        );
      })}

      {/* View more link */}
      <div className="text-center pt-2">
        <Link to="/social" className="text-green-500 font-medium text-sm flex items-center justify-center">
          View Full Social Feed <ArrowRight size={16} className="ml-1" />
        </Link>
      </div>
    </div>
  );
};

export default HomeSocialFeed;